import { useState } from 'react';

import { Box, Heading, Tab, TabList, TabPanel, TabPanels, Tabs, Text } from '@chakra-ui/react';

import { Card } from '~/components/card';
import { trackEvent } from '~/features/analytics/track-event';
import { billingFrequencySchema } from '~/features/checkout/models/checkout';

import { SUBSCRIPTION_SUBTITLE, SUBSCRIPTION_TITLE } from '../constants';

import {
  BundleDisclaimer,
  ExperimentCardShell,
  ExperimentPriceCardProps,
  PriceCardCountdown,
  StandalonePanel,
  SubscriptionPanel,
  useExperimentPriceCard,
} from './shared-price-card';

export function VariantCPriceCard(props: ExperimentPriceCardProps) {
  const state = useExperimentPriceCard(props);
  const [tabIndex, setTabIndex] = useState(0);
  const hasPlans = state.subscriptionPlans.length > 0;
  const isOtpTab = state.visibility.showTabs && tabIndex === 1;
  const effectivePlan = isOtpTab ? billingFrequencySchema.enum.purchase : state.selectedPlan;
  const buttonSlot =
    hasPlans || (isOtpTab && state.standalonePlan) ? props.renderButton(effectivePlan, state.buttonLabel) : null;

  function handleTabChange(index: number) {
    const selectedPlan = index === 1 ? billingFrequencySchema.enum.purchase : state.selectedPlan;
    const label =
      selectedPlan === billingFrequencySchema.enum.purchase
        ? 'onetime_purchase_pricecard_clicked'
        : `${selectedPlan}_pricecard_clicked`;

    trackEvent({
      additionalAttributes: { product_ids: [props.programKey] },
      label,
      name: 'Product Page CTA Clicked',
      type: 'click',
    });
    setTabIndex(index);
  }

  const subscriptionContent = (
    <>
      <Text color='#5A5C62' fontSize='14px' fontWeight='400' lineHeight='20px'>
        {SUBSCRIPTION_SUBTITLE}
      </Text>
      <SubscriptionPanel
        plans={state.subscriptionPlans}
        selectedPlan={state.selectedPlan}
        onSelect={state.setSelectedPlan}
      />
      {hasPlans && (
        <PriceCardCountdown renderButton={props.renderButton} selectedPlan={state.selectedPlan} showSticker />
      )}
      {hasPlans && (
        <BundleDisclaimer bundleDisplayPrice={state.subscriptionPlans[0]?.fullDisplayPrice} isAnnual={state.isAnnual} />
      )}
    </>
  );

  return (
    <ExperimentCardShell
      buttonSlot={buttonSlot}
      imageUrl={props.imageUrl}
      isMobile={props.isMobile}
      promotion={state.promotion}
      renderCover={false}
    >
      {state.visibility.showTabs ? (
        <Tabs index={tabIndex} position='relative' variant='unstyled' onChange={handleTabChange}>
          <TabList height='46px'>
            <PriceCardTab side='left'>Subscription</PriceCardTab>
            <PriceCardTab side='right'>One-Time Purchase</PriceCardTab>
          </TabList>
          <TabPanels>
            <TabPanel padding={0}>
              <CardBody hasTabs>{subscriptionContent}</CardBody>
            </TabPanel>
            <TabPanel padding={0}>
              <CardBody hasTabs>
                <StandalonePanel plan={state.standalonePlan} />
                <PriceCardCountdown
                  renderButton={props.renderButton}
                  selectedPlan={billingFrequencySchema.enum.purchase}
                  showSticker
                />
              </CardBody>
            </TabPanel>
          </TabPanels>
        </Tabs>
      ) : (
        <CardBody>
          <Box>
            <Heading as='p' fontSize='20px' fontWeight='600' letterSpacing='-1px' lineHeight='24px'>
              {SUBSCRIPTION_TITLE}
            </Heading>
          </Box>
          {subscriptionContent}
        </CardBody>
      )}
    </ExperimentCardShell>
  );
}

const PRICE_CARD_STROKE = '#D7E8FF';

function PriceCardTab({ children, side }: { children: string; side: 'left' | 'right' }) {
  const isLeft = side === 'left';
  return (
    <Tab
      bg='#F4F8FF'
      borderBottomWidth='1px'
      borderColor={PRICE_CARD_STROKE}
      borderLeftWidth='1px'
      borderRightWidth='1px'
      borderStyle='solid'
      borderTopLeftRadius={isLeft ? '12px' : 0}
      borderTopRightRadius={isLeft ? 0 : '12px'}
      borderTopWidth='1px'
      color='#5A5C62'
      flex='1'
      fontSize='15px'
      fontWeight='500'
      lineHeight='22px'
      paddingX={2}
      whiteSpace='nowrap'
      _selected={{
        bg: '#F9FBFD',
        borderBottomWidth: 0,
        borderColor: PRICE_CARD_STROKE,
        borderLeftWidth: isLeft ? '1px' : 0,
        borderRightWidth: isLeft ? 0 : '1px',
        color: '#0B0B0B',
        fontWeight: '600',
        zIndex: 1,
      }}
    >
      {children}
    </Tab>
  );
}

function CardBody({ children, hasTabs = false }: { children: React.ReactNode; hasTabs?: boolean }) {
  return (
    <Card
      bg='#F9FBFD'
      borderColor={PRICE_CARD_STROKE}
      borderRadius={hasTabs ? '0 0 12px 12px' : '12px'}
      borderStyle='solid'
      borderTop={hasTabs ? 'none' : undefined}
      borderWidth='1px'
      display='flex'
      flexDirection='column'
      gap={4}
      padding={4}
      position='relative'
    >
      {children}
    </Card>
  );
}
